import React from 'react';

function IconPencil(): React.JSX.Element {
    return (
        <svg xmlns="http://www.w3.org/2000/svg" width={20} height={20} viewBox="0 0 24 24" fill="none" aria-hidden>
            <path
                d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"
                stroke="currentColor"
                strokeWidth={2}
                strokeLinecap="round"
                strokeLinejoin="round"
            />
            <path
                d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"
                stroke="currentColor"
                strokeWidth={2}
                strokeLinecap="round"
                strokeLinejoin="round"
            />
        </svg>
    );
}

function IconTrash(): React.JSX.Element {
    return (
        <svg xmlns="http://www.w3.org/2000/svg" width={20} height={20} viewBox="0 0 24 24" fill="none" aria-hidden>
            <path
                d="M3 6h18M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6"
                stroke="currentColor"
                strokeWidth={2}
                strokeLinecap="round"
                strokeLinejoin="round"
            />
            <path d="M10 11v6M14 11v6" stroke="currentColor" strokeWidth={2} strokeLinecap="round" />
        </svg>
    );
}

const stop = (e: React.MouseEvent) => {
    e.stopPropagation();
};

export function ListActionRow({ children }: { children: React.ReactNode }): React.JSX.Element {
    return <div className="list-action-row">{children}</div>;
}

type IconBtnProps = {
    title?: string;
    onClick: () => void;
};

export function ListEditIconButton({ title = 'Edit', onClick }: IconBtnProps): React.JSX.Element {
    return (
        <button
            type="button"
            className="list-action-icon list-action-icon--edit"
            title={title}
            aria-label={title}
            onClick={(e) => {
                stop(e);
                onClick();
            }}
        >
            <IconPencil />
        </button>
    );
}

export function ListDeleteIconButton({ title = 'Delete', onClick }: IconBtnProps): React.JSX.Element {
    return (
        <button
            type="button"
            className="list-action-icon list-action-icon--delete"
            title={title}
            aria-label={title}
            onClick={(e) => {
                stop(e);
                onClick();
            }}
        >
            <IconTrash />
        </button>
    );
}

type ListEditDeleteActionsProps = {
    onEdit: () => void;
    onDelete?: () => void;
    editTitle?: string;
    deleteTitle?: string;
};

/** Edit + optional delete — same layout as quote / invoice / job lists. */
export function ListEditDeleteActions({
    onEdit,
    onDelete,
    editTitle = 'Edit',
    deleteTitle = 'Delete'
}: ListEditDeleteActionsProps): React.JSX.Element {
    return (
        <ListActionRow>
            <ListEditIconButton title={editTitle} onClick={() => onEdit()} />
            {onDelete ? <ListDeleteIconButton title={deleteTitle} onClick={() => onDelete()} /> : null}
        </ListActionRow>
    );
}

function IconResend(): React.JSX.Element {
    return (
        <svg xmlns="http://www.w3.org/2000/svg" width={20} height={20} viewBox="0 0 24 24" fill="none" aria-hidden>
            <path
                d="M22 2L11 13"
                stroke="currentColor"
                strokeWidth={2}
                strokeLinecap="round"
                strokeLinejoin="round"
            />
            <path
                d="M22 2L15 22l-4-9-9-4 20-7z"
                stroke="currentColor"
                strokeWidth={2}
                strokeLinecap="round"
                strokeLinejoin="round"
            />
        </svg>
    );
}

export function ListResendIconButton({ title = 'Resend', onClick }: IconBtnProps): React.JSX.Element {
    return (
        <button
            type="button"
            className="list-action-icon list-action-icon--resend"
            title={title}
            aria-label={title}
            onClick={(e) => {
                stop(e);
                onClick();
            }}
        >
            <IconResend />
        </button>
    );
}

type ListHistoryActionsProps = {
    onOpen: () => void;
    onResend: () => void;
    onDelete?: () => void;
    openTitle?: string;
    resendTitle?: string;
    deleteTitle?: string;
};

/** Open + resend (+ optional delete) for sent quote/invoice history rows. */
export function ListHistoryActions({
    onOpen,
    onResend,
    onDelete,
    openTitle = 'Open',
    resendTitle = 'Resend',
    deleteTitle = 'Delete'
}: ListHistoryActionsProps): React.JSX.Element {
    return (
        <ListActionRow>
            <ListEditIconButton title={openTitle} onClick={() => onOpen()} />
            <ListResendIconButton title={resendTitle} onClick={() => onResend()} />
            {onDelete ? <ListDeleteIconButton title={deleteTitle} onClick={() => onDelete()} /> : null}
        </ListActionRow>
    );
}

type ListDeleteOnlyProps = {
    onDelete: () => void;
    deleteTitle?: string;
};

export function ListDeleteOnly({ onDelete, deleteTitle = 'Delete' }: ListDeleteOnlyProps): React.JSX.Element {
    return (
        <ListActionRow>
            <ListDeleteIconButton title={deleteTitle} onClick={() => onDelete()} />
        </ListActionRow>
    );
}

type ListPublishButtonProps = {
    onPublish: () => void;
    label?: string;
};

/** Pairs with icon row — same height as edit/delete icons. */
export function ListPublishButton({ onPublish, label = 'Publish' }: ListPublishButtonProps): React.JSX.Element {
    return (
        <button
            type="button"
            className="list-action-btn-publish"
            onClick={(e) => {
                stop(e);
                onPublish();
            }}
        >
            {label}
        </button>
    );
}
